import { intl } from 'lang/intl'
import React, { useCallback } from 'react'

import { TtButton, TtRange, TtIcon, TtTooltip } from '@23/ui-react'

import {
  setEditingFeed,
  updateEditingMix,
  useEditingMixFeed,
  useEditingMixState,
} from '../../../firebase/hooks/useMixes'
import type { Mix } from '../../../firebase/model/mix'
import { copy } from '../../../global/utils/valtio'

const MixSlotPositionMenu = () => {
  const mix = useEditingMixState()
  const { idx: feedIdx } = useEditingMixFeed()
  const feed = feedIdx !== null ? mix?.feeds[feedIdx] : undefined

  const currentZoom = feed?.crop.zoom

  const changeZoom = useCallback(
    (newZoom: number) => {
      if (feedIdx === null) return
      const newMix = copy(mix) as Mix
      const newFeed = newMix.feeds[feedIdx]
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (newFeed) newFeed.crop.zoom = newZoom
      updateEditingMix(newMix)
    },
    [feedIdx, mix]
  )

  const incrementZoom = useCallback(() => {
    if (currentZoom === undefined) return
    const newZoom = Math.max(
      0,
      Math.min(100, (Math.floor(currentZoom / 10) + 1) * 10)
    )
    changeZoom(newZoom)
  }, [changeZoom, currentZoom])

  const decrementZoom = useCallback(() => {
    if (currentZoom === undefined) return
    const newZoom = Math.max(
      0,
      Math.min(100, (Math.ceil(currentZoom / 10) - 1) * 10)
    )
    changeZoom(newZoom)
  }, [changeZoom, currentZoom])

  const reset = useCallback(() => {
    if (feedIdx === null) return
    const newMix = copy(mix) as Mix
    const newFeed = newMix.feeds[feedIdx]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (newFeed)
      newFeed.crop = {
        zoom:
          newFeed.media?.type === 'screen' || newFeed.media?.type === 'tray'
            ? 0
            : 30,
        centerX: 0,
        centerY: 0,
      }
    updateEditingMix(newMix)
  }, [feedIdx, mix])

  if (!feed) return null

  return (
    <div
      ref={(el) => {
        if (el?.popover) el.showPopover()
      }}
      className="mix-slot-position-popover"
      // @ts-expect-error — this is a valid attribute
      popover="manual"
      style={{ positionAnchor: `--slot-${feedIdx}` }}
    >
      <div className="mix-slot-position-menu">
        <TtTooltip content={intl.formatMessage({ defaultMessage: 'Zoom Out' })}>
          <TtButton color="secondary" size="x-small" onClick={decrementZoom}>
            <TtIcon library="one" name="minus" />
          </TtButton>
        </TtTooltip>
        <TtRange
          min={0}
          max={100}
          value={currentZoom}
          tooltip="top"
          tooltipFormatter={(value) => {
            if (value === 0) return 'Fit'
            if (value === 30) return 'Fill'
            if (value < 30) return `${Math.floor((value / 30) * 100)}%`
            return `${Math.floor(((value - 30) / 7) * 4) / 10 + 1}x`
          }}
          label={intl.formatMessage({ defaultMessage: 'Zoom' })}
          hideLabel
          onTtChange={(event) => {
            changeZoom(event.detail.value)
          }}
        />
        <TtTooltip content={intl.formatMessage({ defaultMessage: 'Zoom In' })}>
          <TtButton color="secondary" size="x-small" onClick={incrementZoom}>
            <TtIcon library="one" name="plus" />
          </TtButton>
        </TtTooltip>
        <TtTooltip content={intl.formatMessage({ defaultMessage: 'Reset' })}>
          <TtButton color="secondary" size="x-small" onClick={reset}>
            {/* TODO: temporary icon; replace */}
            <TtIcon library="one/studios" name="resize" />
          </TtButton>
        </TtTooltip>
        <div className="mix-slot-position-menu-vertical-line" />
        <TtTooltip content={intl.formatMessage({ defaultMessage: 'Replace' })}>
          <TtButton
            color="secondary"
            size="x-small"
            onClick={() => {
              setEditingFeed(feedIdx, 'swap')
            }}
          >
            {/* TODO: temporary icon; replace */}
            <TtIcon library="one" name="re-transcode" />
          </TtButton>
        </TtTooltip>
      </div>
    </div>
  )
}

export { MixSlotPositionMenu }
